/**
 * Favorite toggle — TASK 9.2 Step 1 (AC 10.1).
 *
 * The REAL replacement for the mock's `_toggleFavT`/`_toggleFavA`
 * (mock/SteamyMusic.dc.html L905-909) and its `_optim()` fake-latency
 * rollback: ONE optimistic write against the store, ONE persistence call
 * against the favorites API, ONE rollback path. Two exports:
 *
 *   {@link createFavoriteToggle} — the LOGIC. Optimistic apply, announced,
 *       then persisted; on failure the apply is reverted AND an error toast is
 *       raised (AC 10.1's apply→revert sequence). Deliberately shaped exactly
 *       like `modules/app/index.js#toggleFavorite` so the composition root can
 *       delegate to this one implementation instead of keeping a second copy.
 *
 *   {@link FavoriteToggle} — the per-track ♥ BUTTON. Owns the §3 contract
 *       surface for a track heart: `aria-pressed`, an accessible label that
 *       flips between "Favorite <title>" / "Unfavorite <title>", and the
 *       testid flipping between `favoriteTrackButton(title)` and
 *       `unfavoriteTrackButton(title)`. It is a dumb painter on purpose — the
 *       STORE is the single source of favourite truth (every surface reads
 *       `catalog.favorites`), so the button repaints from a store subscription
 *       rather than flipping itself, and an optimistic apply and its rollback
 *       both reach it through the same one channel.
 *
 * ── WHY THE ROLLBACK IS AN INVERSE OP, NOT A SNAPSHOT RESTORE ─────────────
 * `index.js#toggleFavorite` restores the ARRAY captured at tap time. That
 * clobbers any OTHER favourite change that landed while this write was in
 * flight (unfavorite A, then B's failed toggle resurrects A). Reverting by
 * applying the inverse operation to the CURRENT array — remove the id we
 * added, re-add the id we removed — rolls back exactly this toggle and
 * nothing else. Everything else (messages, announce timing, the 404 rule)
 * matches the composition root byte for byte.
 *
 * ── 404 ON REMOVE IS SUCCESS ──────────────────────────────────────────────
 * `DELETE /api/favorites/:type/:id` answers 404 for "unknown, already-removed,
 * or another user's" (routes/favorites.ts) — i.e. the favorite is already
 * absent for this caller, which is what was asked for. Rolling the heart back
 * would re-assert a favorite that does not exist, so a remove-404 keeps the
 * optimistic removal and raises nothing. The branch checks the
 * machine-readable `error.code` first (the api-client contract: "branch on
 * `code`, not `status`") and falls back to `status === 404` for non-ApiError
 * shapes, matching the composition root's existing check.
 *
 * @module
 */

import { ERROR_CODES, favoriteTrackButton, unfavoriteTrackButton } from '@streamymusic/shared';

import { el } from './primitives/dom.js';

/** ui-contract §10.6 — the announced rollback toast (AC 10.1). */
export const FAVORITE_ROLLBACK_MESSAGE = 'Couldn’t save favorite — change rolled back.';
/** The polite announcement for an optimistic add (mock `_toast` copy). */
export const FAVORITE_ADDED_MESSAGE = 'Added to favorites';
/** The polite announcement for an optimistic remove. */
export const FAVORITE_REMOVED_MESSAGE = 'Removed from favorites';

/**
 * Builds the optimistic toggle function every track/album surface shares.
 *
 * @param {{
 *   api: Pick<import('../../api/api-client.js').ApiClient, 'addFavorite'|'removeFavorite'>,
 *   getState: () => import('../app/state-store.js').AppState,
 *   setState: (patch: Partial<import('../app/state-store.js').AppState>
 *     | ((current: import('../app/state-store.js').AppState) => Partial<import('../app/state-store.js').AppState>)) => unknown,
 *   announce: (text: string) => void,
 *   toast: (message: string, kind: string) => void,
 * }} options
 *   `announce` is the shell's persistent polite live region and `toast` its
 *   toast rail (LiveRegions.js) — both REQUIRED, because AC 10.1's "announced
 *   error toast" is part of this function's contract, not an optional nicety.
 * @returns {(type: 'track'|'album', targetId: string) => Promise<void>}
 */
export function createFavoriteToggle(options) {
  const { api, getState, setState, announce, toast } = options;

  /**
   * @param {'tracks'|'albums'} key
   * @param {(current: readonly string[]) => string[]} change
   */
  function write(key, change) {
    setState((state) => ({
      catalog: {
        ...state.catalog,
        favorites: {
          ...state.catalog.favorites,
          [key]: change(state.catalog.favorites[key]),
        },
      },
    }));
  }

  return async function toggleFavorite(type, targetId) {
    const key = type === 'album' ? 'albums' : 'tracks';
    const had = getState().catalog.favorites[key].includes(targetId);

    // The APPLY — synchronous, before any network: the heart flips now.
    write(key, (current) =>
      had ? current.filter((id) => id !== targetId) : [...current, targetId],
    );
    announce(had ? FAVORITE_REMOVED_MESSAGE : FAVORITE_ADDED_MESSAGE);

    try {
      if (had) await api.removeFavorite(type, targetId);
      else await api.addFavorite({ targetType: type, targetId });
    } catch (error) {
      const code = /** @type {{ code?: string }} */ (error)?.code;
      const status = /** @type {{ status?: number }} */ (error)?.status;
      // A remove-404 is success — see the module header. Never roll it back.
      if (had && (code === ERROR_CODES.NOT_FOUND || status === 404)) return;

      // The REVERT — the inverse op against the CURRENT array, so a
      // concurrent toggle of a different target survives this rollback.
      write(key, (current) => {
        if (had) return current.includes(targetId) ? [...current] : [...current, targetId];
        return current.filter((id) => id !== targetId);
      });
      toast(FAVORITE_ROLLBACK_MESSAGE, 'err');
    }
  };
}

/**
 * The per-track ♥ button (mock L312/L406/L491 — the 44×44 transparent heart).
 *
 * `aria-pressed` carries the STATE so it is never colour-only (§8.5), and the
 * tint follows it — the same treatment AlbumDetail.js#paintFavorite gives its
 * rows, kept here so every surface's heart is one implementation.
 *
 * @param {{
 *   title: string,
 *   favorited?: boolean,
 *   onToggle: () => void,
 *   restingColor?: string,
 *   className?: string|string[],
 * }} options
 *   `onToggle` is the surface's wiring to {@link createFavoriteToggle} (or to
 *   the composition root's handler) — the button itself never talks to the
 *   API or the store, so it is reusable from any track surface.
 * @returns {{
 *   element: HTMLElement,
 *   isFavorited: () => boolean,
 *   setFavorited: (next: boolean) => void,
 * }}
 *   `setFavorited` repaints the testid, the ARIA state, the accessible name
 *   and the tint IN PLACE — no rebuild, so focus on the button survives an
 *   optimistic flip and its rollback.
 */
export function FavoriteToggle(options) {
  const {
    title,
    favorited = false,
    onToggle,
    restingColor = 'var(--sm-color-text-dim-2)',
    className = ['sm-btn', 'sm-btn--icon'],
  } = options;

  let state = favorited;

  const button = el('button', {
    className,
    text: '♥',
    attrs: { type: 'button' },
    on: { click: () => onToggle() },
  });

  function paint() {
    button.dataset.testid = state ? unfavoriteTrackButton(title) : favoriteTrackButton(title);
    button.setAttribute('aria-pressed', String(state));
    button.setAttribute('aria-label', `${state ? 'Unfavorite' : 'Favorite'} ${title}`);
    // Dynamic value -> CSSOM, never a style= string (CSP, dom.js contract).
    button.style.setProperty('color', state ? 'var(--sm-color-pink)' : restingColor);
  }
  paint();

  return {
    element: button,
    isFavorited: () => state,
    setFavorited(next) {
      if (next === state) return;
      state = next;
      paint();
    },
  };
}
